import { useContext } from "react";
import {
    useMutation,
    useQuery,
    useQueryClient,
    UseMutationOptions,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { agentRunContract } from "@common/contracts/agent-run.contract";
import {
    AgentRunListResponse,
    AgentRunStartResponse,
    AgentRunSummary,
} from "@common/schemas/domains/agentRuns/agent-run.schema";
import type { TaskActivityListResponse } from "@common/schemas/domains/agentRuns/task-activity.schema";
import { ScopeContext, useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { tasksQueryKeys, todoTasksCountQueryKey } from "@api/task.api";

const agentRunClient = getBackendAPIClient(agentRunContract);

export const agentRunsQueryKeys = {
    all: () => ["agent-runs"] as const,
    list: (workspaceId: string | null | undefined) =>
        [
            ...agentRunsQueryKeys.all(),
            "list",
            workspaceId ?? "anonymous",
        ] as const,
    organizationList: (organizationId: string | null | undefined) =>
        [
            ...agentRunsQueryKeys.all(),
            "organization-list",
            organizationId ?? "anonymous",
        ] as const,
    taskActivityAny: () =>
        [...agentRunsQueryKeys.all(), "task-activity"] as const,
    taskActivityAll: (workspaceId: string | null | undefined) =>
        [
            ...agentRunsQueryKeys.taskActivityAny(),
            workspaceId ?? "anonymous",
        ] as const,
    taskActivity: (workspaceId: string | null | undefined, taskId: string) =>
        [...agentRunsQueryKeys.taskActivityAll(workspaceId), taskId] as const,
};

interface StartAgentRunParams {
    closeId: string;
    entityId: number;
}

type StartAgentRunMutationOptions = Pick<
    UseMutationOptions<AgentRunStartResponse, Error, StartAgentRunParams>,
    "onSuccess" | "onError"
>;

export function useStartAgentRunMutation(
    options?: StartAgentRunMutationOptions,
) {
    const queryClient = useQueryClient();
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useMutation({
        onError: options?.onError,
        mutationFn: async ({
            closeId,
            entityId,
        }: StartAgentRunParams): Promise<AgentRunStartResponse> => {
            if (!workspaceId) {
                throw new Error("No workspace");
            }
            const result = await agentRunClient.start({
                params: { workspaceId },
                body: { closeId, entityId },
            });
            if (result.status !== 201) {
                throw new Error("Failed to start the agent");
            }
            return result.body;
        },
        onSuccess: (...args) => {
            void queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
            });
            void queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
            });
            void queryClient.invalidateQueries({
                queryKey: todoTasksCountQueryKey(activeWorkspaceKey),
            });
            void queryClient.invalidateQueries({
                queryKey: agentRunsQueryKeys.list(workspaceId),
            });
            return options?.onSuccess?.(...args);
        },
    });
}

export function useAgentRuns(options: { enabled?: boolean } = {}) {
    const { workspaceId } = useScope();
    const { agentRuns: isAgentRunsEnabled } = useTypedFlags();

    return useQuery({
        queryKey: agentRunsQueryKeys.list(workspaceId),
        enabled:
            isAgentRunsEnabled && !!workspaceId && (options.enabled ?? true),
        queryFn: async (): Promise<AgentRunListResponse> => {
            if (!workspaceId) {
                throw new Error("No workspace");
            }
            const result = await agentRunClient.list({
                params: { workspaceId },
            });
            if (result.status !== 200) {
                throw new Error("Failed to load agent runs");
            }
            return result.body;
        },
    });
}

/**
 * Every run across the organization's client workspaces the member can see.
 * Reads the root scope: inside a sub-scoped card `useScope()` reports the
 * card's workspace. There is no flag check: organization pages have no
 * workspace in the LaunchDarkly context, so the server is the only gate and
 * its 404 (no visible client has agent runs on) resolves to `null`.
 */
export function useOrganizationAgentRuns({ enabled = true } = {}) {
    const { organizationId, workspaceId } = useContext(ScopeContext);

    return useQuery({
        queryKey: agentRunsQueryKeys.organizationList(organizationId),
        enabled: enabled && !!organizationId && !workspaceId,
        queryFn: async (): Promise<AgentRunListResponse | null> => {
            if (!organizationId) {
                throw new Error("No organization");
            }
            try {
                const result = await agentRunClient.listOrganization({
                    params: { organizationId },
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                });
                return result.body;
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
    });
}

interface TaskActivityParams {
    taskId: string;
    enabled: boolean;
}

// No flag check: only called with a run a gated list returned.
export function useTaskActivity({ taskId, enabled }: TaskActivityParams) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: agentRunsQueryKeys.taskActivity(workspaceId, taskId),
        enabled: !!workspaceId && enabled,
        queryFn: async (): Promise<TaskActivityListResponse> => {
            if (!workspaceId) {
                throw new Error("No workspace");
            }
            const result = await agentRunClient.listTaskActivity({
                params: { workspaceId, taskId },
                query: {},
            });
            if (result.status !== 200) {
                throw new Error("Failed to load activity");
            }
            return result.body;
        },
    });
}

type AgentRunControl = "pause" | "continue" | "stop";

// Errors reach the user through the backendClient toast interceptor; the
// lists are refetched so the row reflects the server's view of the run.
function useAgentRunControlMutation(control: AgentRunControl) {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();
    const { organizationId } = useContext(ScopeContext);

    return useMutation({
        mutationFn: async (runId: string): Promise<AgentRunSummary> => {
            if (!workspaceId) {
                throw new Error("No workspace");
            }
            const result = await agentRunClient[control]({
                params: { workspaceId, runId },
                body: {},
            });
            if (result.status !== 201) {
                throw new Error(`Failed to ${control} the agent`);
            }
            return result.body.run;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: agentRunsQueryKeys.list(workspaceId),
            });
            void queryClient.invalidateQueries({
                queryKey: agentRunsQueryKeys.organizationList(organizationId),
            });
        },
    });
}

export function usePauseAgentRunMutation() {
    return useAgentRunControlMutation("pause");
}

export function useContinueAgentRunMutation() {
    return useAgentRunControlMutation("continue");
}

export function useStopAgentRunMutation() {
    return useAgentRunControlMutation("stop");
}

export interface AgentRunControls {
    onPause: (runId: string) => void;
    onContinue: (runId: string) => void;
    onStop: (runId: string) => void;
    isBusy: boolean;
}

/** The three controls against the scoped workspace, so a sub-scoped row targets its own. */
export function useAgentRunControls(): AgentRunControls {
    const pause = usePauseAgentRunMutation();
    const continueRun = useContinueAgentRunMutation();
    const stop = useStopAgentRunMutation();
    return {
        onPause: pause.mutate,
        onContinue: continueRun.mutate,
        onStop: stop.mutate,
        isBusy: pause.isPending || continueRun.isPending || stop.isPending,
    };
}
